/**
 * AnalysisBuilder Component (Refactored)
 *
 * A redesigned query builder with a modern UX:
 * - Results panel on the left (large)
 * - Query builder panel on the right
 * - Search-based field selection via modal
 * - Sections: Metrics (measures), Breakdown (dimensions), Filters
 * - Auto-execute queries on field changes
 *
 * This refactored version uses:
 * - `useAnalysisBuilder` master hook for all state, data fetching, AI, and share
 *   (AI + share are driven from the facade's `useAnalysisEffects`; this component
 *   no longer calls `useAnalysisAI`/`useAnalysisShare` directly — see #914)
 * - Zustand store (via Context) for state management
 * - TanStack Query (via the master hook) for data fetching
 *
 * ARCHITECTURE: Instance-based stores
 * - Each AnalysisBuilder gets its own Zustand store instance
 * - Standalone mode: Uses localStorage persistence
 * - Modal/portlet editing: No persistence, initializes from props
 */

import { forwardRef, useMemo, useCallback } from 'react'
import { useCubeFeatures, useCubeMeta } from '../../providers/CubeProvider.js'
import { AnalysisBuilderStoreProvider } from '../../stores/analysisBuilderStore.js'
import { useAnalysisBuilder } from '../../hooks/useAnalysisBuilderHook.js'
import { useAnalysisBuilderStoreApi } from '../../stores/analysisBuilderStore.js'
import { parseShareHash, decodeAndDecompress } from '../../utils/shareUtils.js'
import type {
  AnalysisBuilderProps,
  AnalysisBuilderRef,
} from './types.js'
import FieldSearchModal from './FieldSearchModal.js'
import AnalysisResultsPanel from './AnalysisResultsPanel.js'
import AnalysisQueryPanel from './AnalysisQueryPanel.js'
import AnalysisAIPanel from './AnalysisAIPanel.js'
import AnalysisModeErrorBoundary from './AnalysisModeErrorBoundary.js'
import { useAnalysisBuilderImperativeHandle } from './hooks/useAnalysisBuilderImperativeHandle.js'
import {
  extractFunnelStateFromShare,
  extractFlowStateFromShare,
  extractRetentionStateFromShare
} from './utils/shareStateUtils.js'
import type { MetaResponse } from '../../shared/types.js'

/**
 * Inner component that uses the store (must be inside provider)
 */
interface AnalysisBuilderInnerProps extends Omit<AnalysisBuilderProps, 'initialQuery' | 'initialChartConfig' | 'disableLocalStorage'> {
  hideShare?: boolean
}

const AnalysisBuilderInner = forwardRef<AnalysisBuilderRef, AnalysisBuilderInnerProps>(
  (
    {
      className = '',
      maxHeight,
      initialData,
      colorPalette: externalColorPalette,
      hideSettings: _hideSettings = false,
      hideShare = false,
      onQueryChange,
      onChartConfigChange
    },
    ref
  ) => {
    // Mark unused props for future use
    void _hideSettings

    // Get context
    const { meta } = useCubeMeta()
    const { features } = useCubeFeatures()

    // ========================================================================
    // Master Hook - Provides all state, data fetching, and actions
    // ========================================================================
    const analysis = useAnalysisBuilder({
      initialData,
      externalColorPalette,
      onQueryChange,
      onChartConfigChange,
    })

    // ========================================================================
    // AI + Share — now driven by the facade's useAnalysisEffects (#914).
    // The component reads AI/share state and actions off `analysis` directly;
    // no glue lives here anymore. storeApi is still needed for the ref handle.
    // ========================================================================
    const storeApi = useAnalysisBuilderStoreApi()
    const { aiState, shareButtonState } = analysis

    // ========================================================================
    // Derived Values
    // ========================================================================

    // Check if current mode can be cleared
    const canClear = useMemo(() => {
      if (analysis.analysisType === 'funnel') {
        // Funnel mode: can clear if there are steps, cube selected, or configuration
        return (
          analysis.funnelSteps.length > 0 ||
          analysis.funnelCube !== null ||
          analysis.funnelBindingKey !== null ||
          analysis.funnelTimeDimension !== null
        )
      }
      // Query mode: can clear if there are metrics, breakdowns, or filters
      return (
        analysis.queryState.metrics.length > 0 ||
        analysis.queryState.breakdowns.length > 0 ||
        analysis.queryState.filters.length > 0
      )
    }, [
      analysis.analysisType,
      analysis.funnelSteps.length,
      analysis.funnelCube,
      analysis.funnelBindingKey,
      analysis.funnelTimeDimension,
      analysis.queryState.metrics.length,
      analysis.queryState.breakdowns.length,
      analysis.queryState.filters.length
    ])

    // ========================================================================
    // Expose API via ref
    // ========================================================================
    useAnalysisBuilderImperativeHandle(ref, {
      getQueryConfig: analysis.getQueryConfig,
      getChartConfig: analysis.getChartConfig,
      getAnalysisType: analysis.getAnalysisType,
      clearQuery: analysis.actions.clearQuery,
      storeApi
    })

    // ========================================================================
    // Render-time derived values
    // ========================================================================

    // Display config depends on the active analysis mode
    const resultsDisplayConfig =
      analysis.analysisType === 'flow'
        ? analysis.flowDisplayConfig
        : analysis.analysisType === 'funnel'
          ? analysis.funnelDisplayConfig
          : analysis.displayConfig

    // Toggle a metric/breakdown from schema-visualization field clicks
    const handleSchemaFieldClick = useCallback(
      (cubeName: string, fieldName: string, fieldType: string) => {
        const fullName = `${cubeName}.${fieldName}`
        if (fieldType === 'measure') {
          analysis.actions.toggleMetric(fullName)
        } else {
          // Look up whether it's a time dimension from schema
          const cube = (meta as MetaResponse | null)?.cubes?.find((c) => c.name === cubeName)
          const dim = cube?.dimensions?.find((d) => d.name === fullName)
          analysis.actions.toggleBreakdown(fullName, dim?.type === 'time')
        }
      },
      [meta, analysis.actions]
    )

    // ========================================================================
    // Render
    // ========================================================================
    return (
      <div
        className={`dc:flex dc:flex-col dc:lg:flex-row bg-dc-surface dc:border-x dc:border-b border-dc-border ${maxHeight ? 'dc:lg:h-[var(--dc-max-h)] dc:lg:max-h-[var(--dc-max-h)] dc:lg:overflow-hidden' : 'dc:lg:h-full'} ${className}`}
        style={maxHeight ? { ['--dc-max-h' as string]: maxHeight } : undefined}
      >
        {/* Top/Left Panel - Results */}
        <div className="dc:h-[60vh] dc:lg:h-auto dc:lg:flex-1 dc:min-w-0 dc:border-b dc:lg:border-b-0 dc:lg:border-r border-dc-border dc:overflow-auto dc:flex dc:flex-col">
          {/* AI Panel - expands above results when open */}
          {aiState.isOpen && (
            <AnalysisAIPanel
              userPrompt={aiState.userPrompt}
              onPromptChange={analysis.actions.setAIPrompt}
              isGenerating={aiState.isGenerating}
              error={aiState.error}
              hasGeneratedQuery={aiState.hasGeneratedQuery}
              onGenerate={analysis.actions.generateAI}
              onAccept={analysis.actions.acceptAI}
              onCancel={analysis.actions.cancelAI}
            />
          )}

          {/* Results Panel */}
          <div className="dc:flex-1 dc:overflow-auto">
            <AnalysisResultsPanel
              executionStatus={analysis.executionStatus}
              executionResults={analysis.executionResults}
              executionError={analysis.error?.message || null}
              totalRowCount={null}
              resultsStale={analysis.isLoading && analysis.executionResults !== null}
              chartType={analysis.chartType}
              chartConfig={analysis.chartConfig}
              displayConfig={resultsDisplayConfig}
              colorPalette={analysis.colorPalette}
              // Only show palette selector in standalone mode (not when editing portlet)
              currentPaletteName={!externalColorPalette ? analysis.localPaletteName : undefined}
              onColorPaletteChange={!externalColorPalette ? analysis.actions.setLocalPaletteName : undefined}
              allQueries={analysis.allQueries}
              funnelExecutedQueries={analysis.funnelExecutedQueries ?? undefined}
              schema={meta as MetaResponse | null}
              activeView={analysis.activeView}
              onActiveViewChange={analysis.actions.setActiveView}
              displayLimit={analysis.displayLimit}
              onDisplayLimitChange={analysis.actions.setDisplayLimit}
              chartAvailability={analysis.chartAvailability}
              // Debug props - per-query debug data for multi-query mode
              debugDataPerQuery={analysis.debugDataPerQuery}
              // Share props (hidden when viewing shared analysis with initialQuery)
              onShareClick={hideShare ? undefined : analysis.actions.share}
              canShare={hideShare ? false : analysis.isValidQuery}
              shareButtonState={shareButtonState}
              // Refresh props
              onRefreshClick={analysis.actions.refetch}
              canRefresh={analysis.isValidQuery}
              isRefreshing={analysis.isFetching}
              needsRefresh={analysis.needsRefresh}
              // Clear props - use clearCurrentMode to handle both query and funnel modes
              onClearClick={analysis.actions.clearCurrentMode}
              canClear={canClear}
              // AI props
              enableAI={features?.enableAI !== false}
              isAIOpen={aiState.isOpen}
              onAIToggle={aiState.isOpen ? analysis.actions.closeAI : analysis.actions.openAI}
              // Multi-query props
              queryCount={analysis.queryStates.length}
              perQueryResults={analysis.perQueryResults ?? undefined}
              activeTableIndex={analysis.activeTableIndex}
              onActiveTableChange={analysis.actions.setActiveTableIndex}
              // Analysis type (new) - primary way to detect mode
              analysisType={analysis.analysisType}
              // Legacy funnel mode prop (deprecated)
              isFunnelMode={analysis.isFunnelModeEnabled}
              // Funnel debug props
              funnelServerQuery={analysis.funnelServerQuery}
              funnelDebugData={analysis.funnelDebugData}
              // Flow debug props
              flowServerQuery={analysis.flowServerQuery}
              flowDebugData={analysis.flowDebugData}
              // Retention debug props
              retentionServerQuery={analysis.retentionServerQuery}
              retentionDebugData={analysis.retentionDebugData}
              retentionChartData={analysis.retentionChartData}
              retentionValidation={analysis.retentionValidation}
              warnings={analysis.warnings}
              // Schema visualization interaction
              highlightedFields={[
                ...analysis.queryState.metrics.map((m) => m.field),
                ...analysis.effectiveBreakdowns.map((b) => b.field),
              ]}
              onSchemaFieldClick={handleSchemaFieldClick}
            />
          </div>
        </div>

        {/* Bottom/Right Panel - Query Builder */}
        <div className="dc:w-full dc:lg:w-96 dc:flex-shrink-0 dc:lg:h-full dc:overflow-auto dc:lg:overflow-hidden">
          <AnalysisModeErrorBoundary
            analysisType={analysis.analysisType}
            onSwitchToSafeMode={() => analysis.actions.setAnalysisType('query')}
          >
            <AnalysisQueryPanel
            metrics={analysis.queryState.metrics}
            breakdowns={analysis.effectiveBreakdowns}
            filters={analysis.queryState.filters}
            schema={meta as MetaResponse | null}
            activeTab={analysis.activeTab}
            onActiveTabChange={analysis.actions.setActiveTab}
            onAddMetric={analysis.actions.openMetricsModal}
            onRemoveMetric={analysis.actions.removeMetric}
            onReorderMetrics={analysis.actions.reorderMetrics}
            onAddBreakdown={analysis.actions.openBreakdownsModal}
            onRemoveBreakdown={analysis.actions.removeBreakdown}
            onBreakdownGranularityChange={analysis.actions.setBreakdownGranularity}
            onBreakdownComparisonToggle={analysis.actions.toggleBreakdownComparison}
            onReorderBreakdowns={analysis.actions.reorderBreakdowns}
            onFiltersChange={analysis.actions.setFilters}
            onDropFieldToFilter={analysis.actions.dropFieldToFilter}
            order={analysis.queryState.order}
            onOrderChange={analysis.actions.setOrder}
            limit={analysis.queryState.limit}
            onLimitChange={analysis.actions.setLimit}
            chartType={analysis.chartType}
            chartConfig={analysis.chartConfig}
            displayConfig={analysis.displayConfig}
            colorPalette={analysis.colorPalette}
            chartAvailability={analysis.chartAvailability}
            onChartTypeChange={analysis.actions.setChartType}
            onChartConfigChange={analysis.actions.setChartConfig}
            onDisplayConfigChange={analysis.actions.setDisplayConfig}
            validationStatus={analysis.queryState.validationStatus}
            validationError={analysis.queryState.validationError}
            // Multi-query props
            queryCount={analysis.queryStates.length}
            activeQueryIndex={analysis.activeQueryIndex}
            mergeStrategy={analysis.mergeStrategy}
            onActiveQueryChange={analysis.actions.setActiveQueryIndex}
            onAddQuery={analysis.actions.addQuery}
            onRemoveQuery={analysis.actions.removeQuery}
            onMergeStrategyChange={analysis.actions.setMergeStrategy}
            breakdownsLocked={analysis.mergeStrategy === 'merge' && analysis.activeQueryIndex > 0}
            combinedMetrics={analysis.combinedMetrics}
            combinedBreakdowns={analysis.combinedBreakdowns}
            multiQueryValidation={analysis.multiQueryValidation}
            adapterValidation={analysis.adapterValidation}
            // Funnel props (legacy - merge strategy mode)
            funnelBindingKey={analysis.funnelBindingKey}
            onFunnelBindingKeyChange={analysis.actions.setFunnelBindingKey}
            // Analysis Type props (new)
            analysisType={analysis.analysisType}
            onAnalysisTypeChange={analysis.actions.setAnalysisType}
            // Funnel Mode props (new dedicated state)
            funnelCube={analysis.funnelCube}
            funnelSteps={analysis.funnelSteps}
            activeFunnelStepIndex={analysis.activeFunnelStepIndex}
            funnelTimeDimension={analysis.funnelTimeDimension}
            onFunnelCubeChange={analysis.actions.setFunnelCube}
            onAddFunnelStep={analysis.actions.addFunnelStep}
            onRemoveFunnelStep={analysis.actions.removeFunnelStep}
            onUpdateFunnelStep={analysis.actions.updateFunnelStep}
            onSelectFunnelStep={analysis.actions.setActiveFunnelStepIndex}
            onReorderFunnelSteps={analysis.actions.reorderFunnelSteps}
            onFunnelTimeDimensionChange={analysis.actions.setFunnelTimeDimension}
            // Funnel display config (for Display tab in funnel mode)
            funnelDisplayConfig={analysis.funnelDisplayConfig}
            onFunnelDisplayConfigChange={analysis.actions.setFunnelDisplayConfig}
            // Flow Mode props
            flowCube={analysis.flowCube}
            flowBindingKey={analysis.flowBindingKey}
            flowTimeDimension={analysis.flowTimeDimension}
            eventDimension={analysis.eventDimension}
            startingStep={analysis.startingStep}
            stepsBefore={analysis.stepsBefore}
            stepsAfter={analysis.stepsAfter}
            flowJoinStrategy={analysis.joinStrategy}
            onFlowCubeChange={analysis.actions.setFlowCube}
            onFlowBindingKeyChange={analysis.actions.setFlowBindingKey}
            onFlowTimeDimensionChange={analysis.actions.setFlowTimeDimension}
            onEventDimensionChange={analysis.actions.setEventDimension}
            onStartingStepFiltersChange={analysis.actions.setStartingStepFilters}
            onStepsBeforeChange={analysis.actions.setStepsBefore}
            onStepsAfterChange={analysis.actions.setStepsAfter}
            onFlowJoinStrategyChange={analysis.actions.setJoinStrategy}
            flowDisplayConfig={analysis.flowDisplayConfig}
            onFlowDisplayConfigChange={analysis.actions.setFlowDisplayConfig}
            // Retention Mode props (simplified Mixpanel-style)
            retentionCube={analysis.retentionCube}
            retentionBindingKey={analysis.retentionBindingKey}
            retentionTimeDimension={analysis.retentionTimeDimension}
            retentionDateRange={analysis.retentionDateRange}
            retentionCohortFilters={analysis.retentionCohortFilters}
            retentionActivityFilters={analysis.retentionActivityFilters}
            retentionBreakdowns={analysis.retentionBreakdowns}
            retentionViewGranularity={analysis.retentionViewGranularity}
            retentionPeriods={analysis.retentionPeriods}
            retentionType={analysis.retentionType}
            onRetentionCubeChange={analysis.actions.setRetentionCube}
            onRetentionBindingKeyChange={analysis.actions.setRetentionBindingKey}
            onRetentionTimeDimensionChange={analysis.actions.setRetentionTimeDimension}
            onRetentionDateRangeChange={analysis.actions.setRetentionDateRange}
            onRetentionCohortFiltersChange={analysis.actions.setRetentionCohortFilters}
            onRetentionActivityFiltersChange={analysis.actions.setRetentionActivityFilters}
            onRetentionBreakdownsChange={analysis.actions.setRetentionBreakdowns}
            onAddRetentionBreakdown={analysis.actions.addRetentionBreakdown}
            onRemoveRetentionBreakdown={analysis.actions.removeRetentionBreakdown}
            onRetentionViewGranularityChange={analysis.actions.setRetentionViewGranularity}
            onRetentionPeriodsChange={analysis.actions.setRetentionPeriods}
            onRetentionTypeChange={analysis.actions.setRetentionType}
            retentionDisplayConfig={analysis.retentionDisplayConfig}
            onRetentionDisplayConfigChange={analysis.actions.setRetentionDisplayConfig}
          />
          </AnalysisModeErrorBoundary>
        </div>

        {/* Field Search Modal */}
        <FieldSearchModal
          isOpen={analysis.showFieldModal}
          onClose={analysis.actions.closeFieldModal}
          onSelect={analysis.actions.handleFieldSelected}
          mode={analysis.fieldModalMode}
          schema={
            // In retention mode, filter schema to only show the selected cube's fields
            analysis.analysisType === 'retention' && analysis.retentionCube && meta
              ? {
                  ...meta,
                  cubes: meta.cubes?.filter((c) => c.name === analysis.retentionCube) || [],
                } as MetaResponse
              : meta as MetaResponse | null
          }
          selectedFields={[
            ...analysis.queryState.metrics.map((m) => m.field),
            ...analysis.effectiveBreakdowns.map((b) => b.field),
            // Include retention breakdowns in selected fields to show checkmarks
            ...(analysis.analysisType === 'retention' ? analysis.retentionBreakdowns.map((b) => b.field) : [])
          ]}
        />
      </div>
    )
  }
)

AnalysisBuilderInner.displayName = 'AnalysisBuilderInner'

/**
 * AnalysisBuilder - Main exported component
 *
 * Wraps the inner component with the store provider to ensure
 * each AnalysisBuilder instance has its own isolated state.
 */
const AnalysisBuilder = forwardRef<AnalysisBuilderRef, AnalysisBuilderProps>(
  (props, ref) => {
    const {
      initialQuery,
      initialChartConfig,
      initialAnalysisType,
      initialFunnelState,
      initialFlowState,
      initialRetentionState,
      disableLocalStorage = false,
      storageKey,
      ...innerProps
    } = props

    // Parse share URL synchronously to extract initial state before store creation
    // This prevents the flash of wrong view (e.g., chart when share specifies table)
    // and ensures analysisType is correct from the start (prevents useEffect race conditions)
    const shareHash = parseShareHash()
    const sharedState = shareHash ? decodeAndDecompress(shareHash) : null
    const initialActiveViewFromShare = sharedState?.activeView
    const initialAnalysisTypeFromShare = sharedState?.analysisType

    // Phase 3: Extract per-mode initial state from AnalysisConfig format (share URLs)
    const initialFunnelStateFromShare = extractFunnelStateFromShare(sharedState)
    const initialFlowStateFromShare = extractFlowStateFromShare(sharedState)
    const initialRetentionStateFromShare = extractRetentionStateFromShare(sharedState)

    // Hide share button when using initialQuery (e.g., viewing a shared analysis)
    const hideShare = !!initialQuery || !!initialFunnelState || !!initialFlowState || !!initialRetentionState

    return (
      <AnalysisBuilderStoreProvider
        initialQuery={initialQuery}
        initialChartConfig={initialChartConfig}
        initialAnalysisType={initialAnalysisType || initialAnalysisTypeFromShare}
        initialFunnelState={initialFunnelState || initialFunnelStateFromShare}
        initialFlowState={initialFlowState || initialFlowStateFromShare}
        initialRetentionState={initialRetentionState || initialRetentionStateFromShare}
        initialActiveView={initialActiveViewFromShare}
        disableLocalStorage={disableLocalStorage || !!initialQuery || !!initialFunnelState || !!initialFlowState || !!initialRetentionState || !!shareHash}
        storageKey={storageKey}
      >
        <AnalysisBuilderInner ref={ref} {...innerProps} hideShare={hideShare} />
      </AnalysisBuilderStoreProvider>
    )
  }
)

AnalysisBuilder.displayName = 'AnalysisBuilder'

export default AnalysisBuilder
